import { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";

/**
 * Reusable Libraries catalog list + CRUD hook.
 * api: { list, create, update, remove, updateStatus, bulkUpdateStatus }
 */
export default function useLibraryCatalog({
  api,
  defaultSortBy,
  entityLabel = "record",
  extraListParams = null,
}) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    search: "",
    status: "",
    sortBy: defaultSortBy,
    sortOrder: "asc",
  });
  const [searchInput, setSearchInput] = useState("");
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 1,
  });
  const [selectedIds, setSelectedIds] = useState([]);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const result = await api.list({
        page: pagination.page,
        limit: pagination.limit,
        search: filters.search || undefined,
        status: filters.status || undefined,
        sortBy: filters.sortBy,
        sortOrder: filters.sortOrder,
        ...(extraListParams || {}),
      });
      setItems(result?.items || []);
      setPagination((p) => ({
        ...p,
        total: result?.total ?? 0,
        totalPages: result?.totalPages ?? 1,
        page: result?.page ?? p.page,
        limit: result?.limit ?? p.limit,
      }));
      setSelectedIds([]);
    } catch (err) {
      toast.error(err.message || `Failed to load ${entityLabel}s`);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [
    api,
    filters,
    pagination.page,
    pagination.limit,
    entityLabel,
    extraListParams,
  ]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  useEffect(() => {
    const t = setTimeout(() => {
      setFilters((f) => {
        if (f.search === searchInput) return f;
        return { ...f, search: searchInput };
      });
      setPagination((p) => (p.page === 1 ? p : { ...p, page: 1 }));
    }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  const create = useCallback(
    async (payload) => {
      await api.create(payload);
      toast.success(`${entityLabel} created`);
      await fetchList();
    },
    [api, entityLabel, fetchList],
  );

  const update = useCallback(
    async (id, payload) => {
      await api.update(id, payload);
      toast.success(`${entityLabel} updated`);
      await fetchList();
    },
    [api, entityLabel, fetchList],
  );

  const remove = useCallback(
    async (id) => {
      await api.remove(id);
      toast.success(`${entityLabel} deleted`);
      await fetchList();
    },
    [api, entityLabel, fetchList],
  );

  const changeStatus = useCallback(
    async (id, status) => {
      await api.updateStatus(id, status);
      toast.success(`Status set to ${status}`);
      await fetchList();
    },
    [api, fetchList],
  );

  const bulkChangeStatus = useCallback(
    async (status) => {
      if (!selectedIds.length) {
        toast.error("Select at least one row");
        return;
      }
      const result = await api.bulkUpdateStatus(selectedIds, status);
      toast.success(
        `Updated ${result?.updatedCount ?? 0} ${entityLabel}s to ${status}`,
      );
      await fetchList();
    },
    [api, selectedIds, entityLabel, fetchList],
  );

  const clearFilters = useCallback(() => {
    setSearchInput("");
    setFilters({
      search: "",
      status: "",
      sortBy: defaultSortBy,
      sortOrder: "asc",
    });
    setPagination((p) => ({ ...p, page: 1 }));
  }, [defaultSortBy]);

  const toggleSelect = useCallback((id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  }, []);

  const toggleSelectAll = useCallback(() => {
    setSelectedIds((prev) => {
      if (prev.length === items.length) return [];
      return items.map((r) => r.id);
    });
  }, [items]);

  return {
    items,
    loading,
    filters,
    setFilters,
    searchInput,
    setSearchInput,
    pagination,
    setPagination,
    selectedIds,
    toggleSelect,
    toggleSelectAll,
    clearFilters,
    refresh: fetchList,
    create,
    update,
    remove,
    changeStatus,
    bulkChangeStatus,
  };
}
